/**
 * PropertyRequestDetailModal Component
 * Modal showing detailed information about a property request
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useListPublicPaymentAccountsQuery } from "../../../store/api/paymentAccountsApi";
import type { PropertyRequest } from "../../../store/api/propertyRequestsApi";
import { formatDateTime } from "../../../utils/formatDate";

interface PropertyRequestDetailModalProps {
	request: PropertyRequest;
	isOpen: boolean;
	onClose: () => void;
	onAccept: () => void;
	onDecline: () => void;
}

const PropertyRequestDetailModal: React.FC<PropertyRequestDetailModalProps> = ({
	request,
	isOpen,
	onClose,
	onAccept,
	onDecline,
}) => {
	const navigate = useNavigate();
	const { data: accountsData } = useListPublicPaymentAccountsQuery();

	if (!isOpen) return null;

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount);
	};

	// Using centralized formatDateTime utility

	const getStatusBadge = (status: string) => {
		const statusConfig: Record<
			string,
			{ color: string; label: string }
		> = {
			pending: { color: "bg-warning/10 text-warning", label: "Pending" },
			under_review: {
				color: "bg-primary/10 text-primary",
				label: "Under Review",
			},
			accepted: { color: "bg-success/10 text-success", label: "Accepted" },
			declined: { color: "bg-error/10 text-error", label: "Declined" },
			expired: { color: "bg-muted text-muted-foreground", label: "Expired" },
		};

		const config = statusConfig[status] || statusConfig.pending;
		return (
			<span
				className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
			<div className="bg-card border border-border rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
				{/* Header */}
				<div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
					<div>
						<h2 className="text-xl font-bold text-foreground">
							{request.request_type === "lease" ? "Lease" : "Buy"} Request Details
						</h2>
						<p className="text-sm text-muted-foreground mt-1">
							Request ID: {request.id.slice(-8)}
						</p>
					</div>
					<button
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground smooth-transition"
					>
						<Icon name="X" size={24} />
					</button>
				</div>

				{/* Content */}
				<div className="p-6 space-y-6">
					{/* Status */}
					<div className="flex items-center justify-between">
						<span className="text-sm font-medium text-muted-foreground">Status:</span>
						{getStatusBadge(request.status)}
					</div>

					{/* Property Information */}
					<div className="bg-muted/30 rounded-lg p-4">
						<h3 className="font-semibold text-foreground mb-3">Property Information</h3>
						<div className="space-y-2 text-sm">
							<p>
								<span className="text-muted-foreground">Property:</span>{" "}
								<span className="text-foreground font-medium">
									{request.property_name || "N/A"}
								</span>
							</p>
							<p>
								<span className="text-muted-foreground">Request Type:</span>{" "}
								<span className="text-foreground font-medium capitalize">
									{request.request_type}
								</span>
							</p>
							<p>
								<span className="text-muted-foreground">Quoted Amount:</span>{" "}
								<span className="text-foreground font-medium">
									{formatCurrency(request.quoted_amount)}
									{request.request_type === "lease" ? "/month" : ""}
								</span>
							</p>
							{request.offer_amount && (
								<p>
									<span className="text-muted-foreground">Offer Amount:</span>{" "}
									<span className="text-foreground font-medium text-primary">
										{formatCurrency(request.offer_amount)}
										{request.request_type === "lease" ? "/month" : ""}
									</span>
								</p>
							)}
							<p>
								<span className="text-muted-foreground">Willing to Pay Quoted:</span>{" "}
								<span className="text-foreground font-medium">
									{request.willing_to_pay_quoted ? "Yes" : "No"}
								</span>
							</p>
						</div>
					</div>

					{/* Requester Information */}
					<div className="bg-muted/30 rounded-lg p-4">
						<h3 className="font-semibold text-foreground mb-3">Requester Information</h3>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
							<div>
								<p className="text-muted-foreground">Full Name</p>
								<p className="text-foreground font-medium">
									{request.first_name} {request.last_name}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground">Email</p>
								<p className="text-foreground font-medium">{request.email}</p>
							</div>
							<div>
								<p className="text-muted-foreground">Phone</p>
								<p className="text-foreground font-medium">{request.phone}</p>
							</div>
							<div>
								<p className="text-muted-foreground">Employment Status</p>
								<p className="text-foreground font-medium capitalize">
									{request.employment_status?.replace("_", "-") || "N/A"}
								</p>
							</div>
							{request.employer_name && (
								<div>
									<p className="text-muted-foreground">Employer Name</p>
									<p className="text-foreground font-medium">
										{request.employer_name}
									</p>
								</div>
							)}
							{request.business_name && (
								<div>
									<p className="text-muted-foreground">Business Name</p>
									<p className="text-foreground font-medium">
										{request.business_name}
									</p>
								</div>
							)}
							{request.profession && (
								<div>
									<p className="text-muted-foreground">Profession</p>
									<p className="text-foreground font-medium">
										{request.profession}
									</p>
								</div>
							)}
						</div>
					</div>

					{/* Request Details */}
					<div className="bg-muted/30 rounded-lg p-4">
						<h3 className="font-semibold text-foreground mb-3">Request Details</h3>
						<div className="space-y-2 text-sm">
							<p>
								<span className="text-muted-foreground">Submitted:</span>{" "}
								<span className="text-foreground">
									{formatDateTime(request.created_at)}
								</span>
							</p>
							{request.reviewed_at && (
								<p>
									<span className="text-muted-foreground">Reviewed:</span>{" "}
									<span className="text-foreground">
										{formatDateTime(request.reviewed_at)}
									</span>
								</p>
							)}
						</div>
					</div>

					{/* Actions */}
					<div className="flex items-center justify-between space-x-3 pt-4 border-t border-border">
						{/* Chat button - shown when conversation exists */}
						{request.conversation_id && (
							<Button
								variant="outline"
								onClick={() => {
									window.location.href = `/messages?conversation=${request.conversation_id}`;
								}}
								className="flex items-center gap-2"
							>
								<Icon name="MessageCircle" size={16} />
								Open Chat
							</Button>
						)}

						{/* Spacer */}
						<div className="flex-1" />

						{/* Action buttons */}
						{request.status === "pending" ? (
							<div className="flex items-center space-x-3">
								<Button variant="outline" onClick={onClose}>
									Cancel
								</Button>
								<Button variant="outline" onClick={onDecline}>
									<Icon name="X" size={16} className="mr-2" />
									Decline
								</Button>
								<Button onClick={onAccept}>
									<Icon name="Check" size={16} className="mr-2" />
									Accept Request
								</Button>
							</div>
						) : (
							<Button variant="outline" onClick={onClose}>
								Close
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PropertyRequestDetailModal;
